import { Location } from '@angular/common';
import {
	AfterViewInit,
	ChangeDetectorRef,
	Component,
	ComponentFactoryResolver,
	ComponentRef,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	Optional,
	Output,
	SimpleChanges,
	ViewChild,
	ViewContainerRef,
} from '@angular/core';
import { IQuestionnaireItem, IQuestionnaireOverviewItem, Messgeraete } from '@interfaces/interfaces';
import { Router } from '@angular/router';
import { SpecialQuestionItem } from '@interfaces/questions';
import { ISpecialQuestionComponent, SpecialQuestionService } from './special-question.service';
import { Subscription } from 'rxjs';
import { MessplatzService } from '@app/services/messplatz/messplatz.service';
import { WorkflowItemDirective } from '@workflow/directives/workflow-item/workflow-item.directive';
import {tap} from 'rxjs/operators';
import {MissingValueService} from '@app/services/missing-value/missing-value.service';
import {InputService} from '@app/services/input/input.service';
import { firstValueFrom } from '@app/util/rxjs-7-compat';

@Component({
	selector: 'workflow-special-question',
	templateUrl: './special-question.component.html',
	styleUrls: ['./special-question.component.scss'],
})
/* eslint-disable rxjs-angular/prefer-composition */
export class SpecialQuestionComponent implements OnDestroy, OnChanges, AfterViewInit {

	@Input() question: IQuestionnaireItem;
	@Input() overviewConfigs: IQuestionnaireOverviewItem[];
	@Input() isWizard: boolean;
	@Input() hasWorkflowWritePermission: boolean;
	@Input() currentPage: number;
	@Input() workflowItemId: string;
	@Input() groupMissingValueKey: string;
	@Input() epilogUrl: string;
	@Input() prologUrl: string;
	@Input() nextUrl: string;
	@Output() valid: EventEmitter<boolean> = new EventEmitter();
	@Input() finishedAndLocked = false;
	@Input() wizardLink: string;
	modalVisible: boolean;

	@ViewChild('container', {read: ViewContainerRef}) container: ViewContainerRef;

	private component: ComponentRef<ISpecialQuestionComponent>;
	private subscriptions: Subscription = new Subscription();
	private itemSub: Subscription;

	specialQuestion: SpecialQuestionItem;
	missingDevices: Messgeraete[];
	hasAllDevices: boolean;
	hasGroupMissingValue: boolean;
	hasGroupOverview: boolean;
	softLocked = false;
	validationLevel: 'error' | 'warning' = 'error';

	constructor(
		location: Location,
		private router: Router,
		private componentFactoryResolver: ComponentFactoryResolver,
		private changeDetectorRef: ChangeDetectorRef,
		private specialQuestionService: SpecialQuestionService,
		private messplatzService: MessplatzService,
		private inputService: InputService,
		@Optional() private workflowItemDirective: WorkflowItemDirective,
	) {
		//TODO ordentlicher Link
		this.wizardLink = location.path();
		this.wizardLink = this.wizardLink.replace('compact', 'wizard');
	}

	ngOnDestroy(): void {
		this.subscriptions.unsubscribe();
		this.component?.destroy();
		this.inputService.suspendDetectionSwipe = false;
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.question?.firstChange || changes.currentPage || changes.hasWorkflowWritePermission) {
			this.specialQuestion = this.question as SpecialQuestionItem;
			const requiredDevices: Messgeraete[] = this.specialQuestionService.getSpecialQuestion(this.specialQuestion).requiredDevices;
			this.missingDevices = this.messplatzService.getMissingDevices(requiredDevices);
			this.hasAllDevices = this.missingDevices?.length === 0;
			this.loadComponent();
		}
		else {
			changes.page = changes.currentPage;
			this.syncInstance();
		}
		if (this.component?.instance?.ngOnChanges) {
			this.component.instance.ngOnChanges(changes);
		}
	}

	ngAfterViewInit(): void {
		this.loadComponent();
		this.changeDetectorRef.detectChanges();
		if (this.component?.instance?.ngAfterViewInit) {
			this.component.instance.ngAfterViewInit();
		}
		firstValueFrom(this.workflowItemDirective.current$).then(item => {
			const question = this.specialQuestionService.getSpecialQuestion(this.specialQuestion);
			this.softLocked = question?.isCompleted(item?.getProperty(this.specialQuestion.key));
			this.validationLevel = this.softLocked ? 'warning' : 'error';
		});
	}

	private hasPrimitiveValues(input: unknown): boolean {
		if (typeof input === 'number' || typeof input === 'string' || typeof input === 'bigint') {
			return true;
		}
		if (typeof input === 'object' && input !== null) {
			for (const key in input) {
				if (Object.prototype.hasOwnProperty.call(input, key) && this.hasPrimitiveValues(input[key])) {
					return true;
				}
			}
		}
		return false;
	}

	showCorrectPercentage(num: number): string {
		return (Math.round(num * 100) / 100).toFixed(2);
	}

	public setValid(valid: boolean): void {
		this.valid.emit(valid);
	}

	showWizardLink(): void {
		if (!this.isWizard && this.wizardLink) {
			this.router.navigate([this.wizardLink]);
		}
	}

	private loadComponent() {
		this.container?.clear();
		this.component?.destroy();
		this.inputService.suspendDetectionSwipe = false;

		if (this.hasWorkflowWritePermission) {
			const question = this.specialQuestionService.getSpecialQuestion(this.specialQuestion);
			if (question.component && this.container) {
				const componentFactory = this.componentFactoryResolver.resolveComponentFactory(question.component);

				this.component = this.container.createComponent<ISpecialQuestionComponent>(componentFactory, undefined, this.container.injector);

				this.syncInstance();
				const instance = this.component.instance;
				this.inputService.suspendDetectionSwipe = true;

				//Das erste ngOnChanges bei Komponent Erstellung
				if (instance.ngOnChanges) {
					instance.ngOnChanges({
						question: {previousValue: null, currentValue: instance.question, firstChange: true, isFirstChange(): boolean { return true; }},
						page: {previousValue: null, currentValue: instance.page, firstChange: true, isFirstChange(): boolean { return true; }},
						workflowItemId: {previousValue: null, currentValue: instance.workflowItemId, firstChange: true, isFirstChange(): boolean { return true; }},
						epilogUrl: {previousValue: null, currentValue: instance.epilogUrl, firstChange: true, isFirstChange(): boolean { return true; }},
						prologUrl: {previousValue: null, currentValue: instance.prologUrl, firstChange: true, isFirstChange(): boolean { return true; }},
						nextUrl: {previousValue: null, currentValue: instance.nextUrl, firstChange: true, isFirstChange(): boolean { return true; }},
						finishedAndLocked: {previousValue: null, currentValue: instance.finishedAndLocked, firstChange: true, isFirstChange(): boolean { return true; }},
						overviewConfigs: {previousValue: null, currentValue: instance.overviewConfigs, firstChange: true, isFirstChange(): boolean { return true; }},
					});
				}

				const sub = new Subscription();
				sub.add(this.component.instance.valid.subscribe((event: boolean) => this.setValid(event)));
				this.component.onDestroy(() => sub.unsubscribe());

			} else {

			}

			if (!this.itemSub && this.workflowItemDirective) {
				const item$ = this.finishedAndLocked ? this.workflowItemDirective.itemFull$ : this.workflowItemDirective.item$;
				this.itemSub = item$.pipe(
					tap(item => this.hasGroupMissingValue = MissingValueService.isMissingValue({
						missingValueKey: (this.specialQuestion.baseKey || this.specialQuestion.key) + '.' + question.missingValueKeyGroup
					}, item)),
					tap(item => {
						if (!this.isWizard && this.specialQuestion && this.modalVisible === undefined) {
							this.modalVisible = this.hasPrimitiveValues(item?.getProperty(this.specialQuestion.key));
						}
					})
				).subscribe();
				this.subscriptions.add(this.itemSub);
			}
		}
	}

	private syncInstance() {
		if (this.component) {
			const instance = this.component.instance;
			instance.question = this.question;
			instance.page = this.currentPage;
			instance.workflowItemId = this.workflowItemId;
			instance.epilogUrl = this.epilogUrl;
			instance.prologUrl = this.prologUrl;
			instance.nextUrl = this.nextUrl;
			instance.finishedAndLocked = this.finishedAndLocked;
			instance.overviewConfigs = this.overviewConfigs;
		}
	}

	setOpenAlternateInput(): void {
		this.modalVisible = !this.modalVisible;
	}

	setCloseAlternateInput(): void {
		this.modalVisible = false;
	}

	removeSoftLock(): void {
		this.softLocked = false;
		this.validationLevel = 'error';
	}
}
